/**
 * When a schedule runs, said as one sentence (Feature 81 D2): a choice of
 * six and only the fields it needs, times in the viewer's own; and one
 * quiet line under it with the UTC cron the sentence makes, its reading,
 * and its next fire (D7), and the months a day past 28 skips (revision 1,
 * D16). The cron stays the value the form holds and
 * sends: the builder opens on it in the choice that makes it, or as typed,
 * and reports a new one only on an edit (D11). Switching choice keeps
 * what the sentence has said (D12).
 */
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { isValidCron, nextSlots, slotText, viewerTimeZone } from '../../lib/cron.js';
import {
  type Every,
  MINUTE_STEPS,
  MONTH_DAYS,
  type Schedule,
  type ScheduleCron,
  cronReading,
  monthsSkipped,
  scheduleCron,
  scheduleOf,
  scheduleWords,
} from '../../lib/schedule.js';
import { Input, Typography } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { DayToggles } from './DayToggles.js';

const CHOICES: readonly { value: Every; label: string }[] = [
  { value: 'minutes', label: 'Every few minutes' },
  { value: 'hour', label: 'Every hour' },
  { value: 'day', label: 'Every day' },
  { value: 'week', label: 'Every week' },
  { value: 'month', label: 'Every month' },
  { value: 'custom', label: 'Custom cron' },
];
const MINUTES = Array.from({ length: 60 }, (_, m) => ({
  value: String(m),
  label: String(m).padStart(2, '0'),
}));
const STEP_OPTIONS = MINUTE_STEPS.map(String);
const MONTH_DAY_OPTIONS = Array.from({ length: MONTH_DAYS }, (_, i) => String(i + 1));
// The day before a day past 28 in UTC, and the months that have it but not the day (D17).
const EVENING_BEFORE: Record<number, string> = {
  29: 'the 28th, which February has every year: outside a leap year it would fire on 1 March',
  30: 'the 29th, which February has in a leap year: then it would fire on 1 March',
  31: 'the 30th, which April, June, September, and November have too: after them it would fire on the 1st',
};
const ROW = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 } as const;

/** Every field the sentence has said, kept across choices; `cron` is the last made, or Custom's text. */
interface Sentence {
  every: Every;
  step: number;
  minute: number;
  time: string;
  days: readonly number[];
  day: number;
  cron: string;
}

/** What an empty cron opens on: every day at 09:00 the viewer's time. */
const DEFAULT: Sentence = {
  every: 'day',
  step: 15,
  minute: 0,
  time: '09:00',
  days: [1, 2, 3, 4, 5],
  day: 1,
  cron: '',
};

interface Props {
  /** The choice's id, so a Field label names the sentence. */
  id: string;
  /** The UTC cron held; empty when there is none yet. */
  cron: string;
  /** A new cron on an edit, or null while the sentence makes none. */
  onChange: (cron: string | null) => void;
  /** The viewer's timezone unless a test names one. */
  timeZone?: string;
}

/** The schedule a sentence says. */
function scheduleFrom(s: Sentence): Schedule {
  switch (s.every) {
    case 'minutes':
      return { every: 'minutes', step: s.step };
    case 'hour':
      return { every: 'hour', minute: s.minute };
    case 'day':
      return { every: 'day', time: s.time };
    case 'week':
      return { every: 'week', days: s.days, time: s.time };
    case 'month':
      return { every: 'month', day: s.day, time: s.time };
    case 'custom':
      return { every: 'custom', cron: s.cron };
  }
}

/** The sentence a held cron opens as; an empty one opens on the default and its cron. */
function sentenceOf(cron: string, timeZone: string): Sentence {
  const now = new Date();
  if (!cron.trim()) {
    return { ...DEFAULT, cron: scheduleCron(scheduleFrom(DEFAULT), timeZone, now).cron ?? '' };
  }
  return { ...DEFAULT, ...scheduleOf(cron, timeZone, now), cron };
}

/** The value a sentence reports: Custom's text as typed, any other choice's cron. */
function reportOf(sentence: Sentence, made: ScheduleCron): string | null {
  return sentence.every === 'custom' ? sentence.cron.trim() || null : made.cron;
}

/** The timezone as the sentence names it beside a time: "Bangkok time", or UTC. */
function zoneWords(timeZone: string): string {
  if (timeZone === 'UTC' || timeZone === 'Etc/UTC') return 'UTC';
  return `${(timeZone.split('/').pop() ?? timeZone).replaceAll('_', ' ')} time`;
}

/** Why the sentence makes no cron, said under it. */
function whyNone(made: ScheduleCron, day: number): string {
  if (made.cron !== null) return '';
  switch (made.reason) {
    case 'no_day':
      return 'Pick at least one day.';
    case 'no_time':
      return 'Pick a time.';
    case 'month_day_leaves': {
      const before = EVENING_BEFORE[day];
      if (made.side === 'later' && before) {
        return `In UTC that is the evening of ${before}. Pick ${made.nearest} or later, or a day up to 28.`;
      }
      return made.side === 'later'
        ? `In UTC that is the evening before day ${day}, which no single cron can say. Pick ${made.nearest} or later, or another day.`
        : `In UTC that is the morning after day ${day}, which no single cron can say. Pick ${made.nearest} or earlier, or another day.`;
    }
    case 'invalid':
      return `Pick a day from 1 to ${MONTH_DAYS}.`;
  }
}

/** The quiet line: the cron as saved, in UTC, its reading, and its next fire. */
function resultLine(cron: string, reading: string | null, timeZone: string): ReactElement {
  const next = nextSlots(cron, new Date(), 1)[0];
  return (
    <Typography variant="meta" style={{ ...ROW, gap: 5 }}>
      <span
        className="mono"
        style={{
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--text-strong)',
          background: 'var(--surface-sunken)',
          borderRadius: 'var(--radius-chip)',
          padding: '2px 5px',
        }}
      >
        {cron}
      </span>
      <span>
        {reading ?? 'No plain reading'} ·{' '}
        {next ? `next ${slotText(next, timeZone)}` : 'no fire within four years'}
      </span>
    </Typography>
  );
}

export function ScheduleBuilder({
  id,
  cron,
  onChange,
  timeZone = viewerTimeZone(),
}: Props): ReactElement {
  const [sentence, setSentence] = useState(() => sentenceOf(cron, timeZone));
  const reported = useRef(cron);
  const made = useMemo(
    () => scheduleCron(scheduleFrom(sentence), timeZone, new Date()),
    [sentence, timeZone],
  );

  // A cron set from outside, not one this sentence reported, opens afresh.
  useEffect(() => {
    if (cron === reported.current) return;
    reported.current = cron;
    setSentence(sentenceOf(cron, timeZone));
  }, [cron, timeZone]);

  // An empty cron has nothing to keep, so the default it opens on is reported once.
  useEffect(() => {
    if (cron.trim() || !sentence.cron) return;
    reported.current = sentence.cron;
    onChange(sentence.cron);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on opening
  }, []);

  const edit = (patch: Partial<Sentence>) => {
    const next = { ...sentence, ...patch };
    const result = scheduleCron(scheduleFrom(next), timeZone, new Date());
    // Custom keeps its text; any other choice's last cron is what Custom starts from.
    if (next.every !== 'custom' && result.cron !== null) next.cron = result.cron;
    const value = reportOf(next, result);
    setSentence(next);
    reported.current = value ?? '';
    onChange(value);
  };

  const custom = sentence.every === 'custom';
  const skipped =
    sentence.every === 'month' && made.cron !== null ? monthsSkipped(sentence.day) : null;
  const typed = sentence.cron.trim();
  const at = (
    <span style={ROW}>
      at
      <Input
        type="time"
        aria-label="Time"
        value={sentence.time}
        onChange={(e) => edit({ time: e.target.value })}
        full={false}
        wrapperStyle={{ width: 130 }}
      />
      <Typography variant="meta">{zoneWords(timeZone)}</Typography>
    </span>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        fontSize: 'var(--text-body-sm)',
        color: 'var(--text-soft)',
      }}
    >
      <div style={ROW}>
        <Select
          id={id}
          aria-label="Repeats"
          width={164}
          value={sentence.every}
          options={CHOICES}
          onChange={(every) => edit({ every: every as Every })}
        />
        {sentence.every === 'minutes' && (
          <span style={ROW}>
            <Select
              aria-label="Minutes apart"
              width={70}
              value={String(sentence.step)}
              options={STEP_OPTIONS}
              onChange={(step) => edit({ step: Number(step) })}
            />
            {sentence.step === 1 ? 'minute apart' : 'minutes apart'}
          </span>
        )}
        {sentence.every === 'hour' && (
          <span style={ROW}>
            at minute
            <Select
              aria-label="Minute"
              width={76}
              value={String(sentence.minute)}
              options={MINUTES}
              onChange={(minute) => edit({ minute: Number(minute) })}
            />
          </span>
        )}
        {sentence.every === 'day' && at}
        {sentence.every === 'week' && (
          <span style={ROW}>
            on
            <DayToggles days={sentence.days} onChange={(days) => edit({ days })} />
          </span>
        )}
        {sentence.every === 'month' && (
          <span style={ROW}>
            on day
            <Select
              aria-label="Day of the month"
              width={70}
              value={String(sentence.day)}
              options={MONTH_DAY_OPTIONS}
              onChange={(day) => edit({ day: Number(day) })}
            />
          </span>
        )}
        {sentence.every === 'month' && at}
        {custom && (
          <span style={ROW}>
            <Input
              aria-label="Cron, UTC"
              value={sentence.cron}
              placeholder="0 2 * * 1-5"
              onChange={(e) => edit({ cron: e.target.value })}
              full={false}
              wrapperStyle={{ width: 180 }}
              // The console's Input replaces any class, and finixt-ui writes the sans font inline.
              style={{ fontFamily: 'var(--font-mono)' }}
            />
            <Typography variant="meta">UTC</Typography>
          </span>
        )}
      </div>
      {sentence.every === 'week' && at}
      {custom && typed && !isValidCron(typed) && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          Not five cron fields read in UTC, such as 0 2 * * 1-5.
        </Typography>
      )}
      {custom &&
        typed &&
        isValidCron(typed) &&
        resultLine(typed, cronReading(typed, timeZone), timeZone)}
      {!custom &&
        made.cron !== null &&
        resultLine(made.cron, scheduleWords(scheduleFrom(sentence)), timeZone)}
      {skipped && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          {skipped}
        </Typography>
      )}
      {!custom && made.cron === null && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          {whyNone(made, sentence.day)}
        </Typography>
      )}
    </div>
  );
}
